import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Eye, X } from 'lucide-react';

import { getPreviewPosition } from './ScriptPreview.position';
import {
  Card,
  Chip,
  CloseButton,
  Counts,
  Divider,
  EmptyMessage,
  Header,
  Name,
  Stage,
  StageList,
  StageNumber,
  TriggerButton,
} from './ScriptPreview.style';

export interface ScriptPreviewOption {
  id: number;
  name: string;
  stages?: string[];
  materialsCount?: number;
}

// o cartão abre uma coluna nova à direita a cada bloco deste tamanho, em vez de alongar a lista.
const STAGES_PER_COLUMN = 15;
// além de três colunas o cartão passa de 860px e não cabe mais à esquerda do dropdown; daí em
// diante a última coluna rola.
const MAX_COLUMNS = 3;

// o portal da lista é o ancestral rolável mais próximo do olho (overflow-y: auto).
const findScrollableAncestor = (element: HTMLElement): HTMLElement | null => {
  let node = element.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
};

interface ScriptPreviewCardProps {
  option: ScriptPreviewOption;
  anchorRef: React.RefObject<HTMLButtonElement>;
  onClose: () => void;
}

const ScriptPreviewCard = ({ option, anchorRef, onClose }: ScriptPreviewCardProps): JSX.Element => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const anchorElement = anchorRef.current;
    const card = cardRef.current?.getBoundingClientRect();
    if (!anchorElement || !card) return;

    const anchor = anchorElement.getBoundingClientRect();
    const list = findScrollableAncestor(anchorElement)?.getBoundingClientRect() ?? anchor;

    setPosition(getPreviewPosition(list, anchor, { width: card.width, height: card.height }));
  }, [anchorRef, option.id]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    // rolar as etapas dentro do cartão não pode fechá-lo; só o scroll de fora fecha.
    const handleScroll = (event: Event) => {
      if (cardRef.current?.contains(event.target as Node)) return;
      onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    // scroll não borbulha: capture é a única forma de ouvir o do portal da lista.
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [onClose]);

  const stages = option.stages ?? [];
  const materialsCount = option.materialsCount ?? 0;
  const columns = Math.min(MAX_COLUMNS, Math.max(1, Math.ceil(stages.length / STAGES_PER_COLUMN)));

  return ReactDOM.createPortal(
    <Card
      ref={cardRef}
      $columns={columns}
      role="dialog"
      aria-label={`Composição do script ${option.name}`}
      style={{
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? 'visible' : 'hidden',
      }}
      // preventDefault segura o foco no input, já que o stopPropagation impede o do OptionsList
      // de rodar; stopPropagation impede o handleClickOutside (em document) de fechar a lista.
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      // na árvore do React o cartão é descendente do OptionItem: sem isto o clique seleciona.
      onClick={(event) => event.stopPropagation()}
    >
      <Header>
        <Name>{option.name}</Name>
        <Chip>ID: {option.id}</Chip>
        <CloseButton type="button" aria-label="Fechar" onClick={onClose}>
          <X size={14} />
        </CloseButton>
      </Header>

      <Divider />

      <Counts>
        <Chip>
          {stages.length} {stages.length === 1 ? 'serviço' : 'serviços'}
        </Chip>
        <Chip>
          {materialsCount} {materialsCount === 1 ? 'matéria-prima' : 'matérias-primas'}
        </Chip>
      </Counts>

      {stages.length > 0 ? (
        <StageList $columns={columns}>
          {stages.map((stage, index) => (
            <Stage key={`${option.id}-${index}-${stage}`}>
              <StageNumber>{index + 1}.</StageNumber>
              <span>{stage}</span>
            </Stage>
          ))}
        </StageList>
      ) : (
        <EmptyMessage>Nenhuma etapa cadastrada</EmptyMessage>
      )}
    </Card>,
    document.body,
  );
};

interface ScriptPreviewTriggerProps {
  option: ScriptPreviewOption;
  isOpen: boolean;
  onToggle: (scriptId: number | null) => void;
}

const ScriptPreviewTrigger = ({ option, isOpen, onToggle }: ScriptPreviewTriggerProps): JSX.Element => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  const handleClose = useCallback(() => onToggle(null), [onToggle]);

  // o estado do cartão mora no wrapper e sobrevive ao fechamento da lista; sem esta limpeza
  // ele reabriria sozinho na próxima vez que o dropdown abrisse.
  useEffect(
    () => () => {
      if (isOpenRef.current) onToggle(null);
    },
    [onToggle],
  );

  return (
    <>
      <TriggerButton
        ref={anchorRef}
        type="button"
        aria-label={`Ver composição do script ${option.name}`}
        aria-expanded={isOpen}
        // só no clique: no mousedown cancelaria o preventDefault do OptionsList e a lista
        // fecharia por blur.
        onClick={(event) => {
          event.stopPropagation();
          onToggle(isOpen ? null : option.id);
        }}
      >
        <Eye size={14} />
      </TriggerButton>

      {isOpen && <ScriptPreviewCard option={option} anchorRef={anchorRef} onClose={handleClose} />}
    </>
  );
};

export default ScriptPreviewTrigger;
